import * as React from 'react';
import AutosizeInput from 'react-input-autosize';
import styled from 'styled-components';
import { Button, Tooltip, Colors, AnalyticsComponent } from '@inplayer-org/inplayer-ui';
import { IoMdTrash } from 'react-icons/io';

// utils
import { unescapeText } from 'utils/helpers';

// components
import Restricted from 'restrictions/roles/components/Restricted';
import { StyledReactIcon } from 'pages/MerchantPages/Audience/components/AudienceDetailsBox';
import CommonIcon from 'components/CommonIcon';

const Header = styled.header<{ affix: boolean }>`
  position: ${({ affix }) => (affix ? 'fixed' : 'relative')};
  padding: 20px 62px;
  background: #fff;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  padding: 0 18px;
`;

const ButtonWrapper = styled.div`
  margin: 0 1rem;
`;

const StyledIcon = styled(StyledReactIcon)`
  vertical-align: middle;

  :hover {
    transform: scale(1.2);
    transition: 0.2s ease;
  }
`;

const InputIconWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 2rem;
`;

const StyledAutosizeInput = styled(AutosizeInput)`
  &&& {
    margin-left: 10px;
    &:hover {
      border-bottom: 1px solid #d5dbe1;
    }
    &:focus,
    &:active {
      border-bottom: 1px solid #00abd3;
    }
    input {
      ::placeholder,
      ::-webkit-input-placeholder {
        font-size: 20px;
        color: #cfd6dc;
        font-weight: lighter;
      }

      ::-moz-placeholder {
        font-size: 20px;
        color: #cfd6dc;
        font-weight: lighter;
      }

      :-ms-input-placeholder {
        font-size: 20px;
        color: #cfd6dc;
        font-weight: lighter;
      }
    }
  }
`;

interface Props {
  brandingDetails: any;
  cancel: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  save: (event: React.MouseEvent<HTMLButtonElement>) => void;
  showDeleteModal?: () => void;
  isReadOnly?: boolean;
}

interface State {
  title: string;
  affix: boolean;
}

export default class SaveDraft extends React.PureComponent<Props, State> {
  state = { title: '', affix: false };

  static defaultProps = { isReadOnly: false };

  componentDidMount() {
    const {
      brandingDetails: { brand_name: brandName },
    } = this.props;

    this.setState(() => ({ title: unescapeText(brandName) }));

    window.addEventListener('scroll', this.handleScroll);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      brandingDetails: { brand_name: brandName, id },
    } = prevProps;

    const {
      brandingDetails: { id: nextId, brand_name: nextBrandName },
    } = this.props;

    if (id !== nextId) {
      if (brandName) {
        this.setState(() => ({
          title: unescapeText(nextBrandName),
        }));
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  getInputValues = () => {
    const { title } = this.state;
    return title;
  };

  titleChange = (e: React.SyntheticEvent) => {
    this.setState({ title: (e.target as HTMLInputElement).value });
  };

  handleScroll = () => {
    const { affix } = this.state;
    const offset = 46;
    const scrollTop = (document.documentElement && document.documentElement.scrollTop) || document.body.scrollTop;

    if (!affix && scrollTop >= offset) {
      this.setState({
        affix: true,
      });
    }

    if (affix && scrollTop < offset) {
      this.setState({
        affix: false,
      });
    }
  };

  addStyle = (e: React.SyntheticEvent) => {
    (e.target as HTMLInputElement).style.borderBottom = '1px solid rgba(0,171,211,1)';
  };

  removeStyle = (e: React.SyntheticEvent) => {
    (e.target as HTMLInputElement).style.borderBottom = '1px solid rgba(255,255,255,1)';
  };

  render() {
    const {
      brandingDetails: { id },
      cancel,
      save,
      showDeleteModal,
      isReadOnly = false,
    } = this.props;

    const { affix, title } = this.state;

    return (
      <Header affix={affix}>
        <ButtonsWrapper>
          <AnalyticsComponent>
            {({ pages, tracker, merchantId, ip }) => (
              <a
                href="#/"
                onClick={(e) => {
                  tracker.track({
                    event: 'click',
                    type: 'button',
                    tag: 'button_cancel',
                    pages,
                    merchantId,
                    ip,
                  });
                  cancel(e);
                }}
              >
                Cancel
              </a>
            )}
          </AnalyticsComponent>
          <Restricted isRestricted={isReadOnly}>
            <ButtonWrapper>
              <Button tag="button_save" fullWidth type="button" onClick={save}>
                Save
              </Button>
            </ButtonWrapper>
            {id !== 0 && id !== undefined ? (
              <AnalyticsComponent>
                {({ pages, tracker, merchantId, ip }) => (
                  <Tooltip placement="left" content="Are you sure you want to delete the branding theme">
                    <StyledIcon
                      as={IoMdTrash}
                      color={Colors.red}
                      onClick={() => {
                        tracker.track({
                          event: 'click',
                          type: 'icon',
                          tag: 'icon_delete',
                          pages,
                          merchantId,
                          ip,
                        });
                        return showDeleteModal?.();
                      }}
                    />
                  </Tooltip>
                )}
              </AnalyticsComponent>
            ) : null}
          </Restricted>
        </ButtonsWrapper>
        <InputIconWrapper>
          <CommonIcon name="edit" isIconDisabled />
          <StyledAutosizeInput
            type="text"
            placeholder="Name your branding theme"
            placeholderIsMinWidth
            value={title}
            onChange={this.titleChange}
            onFocus={this.addStyle}
            onBlur={this.removeStyle}
            readOnly={isReadOnly}
            inputStyle={{
              width: 'auto',
              minWidth: '530px',
              maxWidth: '980px',
              padding: 0,
              margin: 0,
              color: '#333333',
              fontSize: '28px',
              fontWeight: 300,
              border: 'none',
              borderBottom: '1px solid rgba(255,255,255,1)',
              zIndex: 100,
              fontFamily: 'Roboto',
            }}
          />
        </InputIconWrapper>
      </Header>
    );
  }
}
